import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { QuestionMarkCircleIcon } from '@heroicons/react/solid';
import { useTranslation } from 'react-i18next';
import convertToEditorState from '../../../../utils/convertToEditorState';
import covertEditorStateToJson from '../../../../utils/covertEditorStateToJson';
import TextEditor from '../../../../components/editor';
import Item from '../../../../types/elearning/item';

type PanelProps = {
  item: Item;
  setItem: (item: Item) => void;
};

export function Panel(props: PanelProps) {
  const { item, setItem } = props;
  const { t } = useTranslation();

  const [answerEditorState, setAnswerEditorState] = useState(
    convertToEditorState(item.content.answer || '')
  );
  const [questionEditorState, setQuestionEditorState] = useState(
    convertToEditorState(item.content.question || '')
  );
  const [explanationEditorState, setExplanationEditorState] = useState(
    convertToEditorState(item.content.explanation || '')
  );

  useEffect(() => {
    // Update the strinified JSON representation of the rich text editor
    const newItem = { ...item };
    newItem.content.answer = covertEditorStateToJson(answerEditorState);
    newItem.content.explanation = covertEditorStateToJson(
      explanationEditorState
    );
    newItem.content.question = covertEditorStateToJson(questionEditorState);
    setItem(newItem);
  }, [answerEditorState, questionEditorState, explanationEditorState]);

  return (
    <div className="w-full flex justify-center">
      <div className="flex flex-col space-y-5 max-w-xl w-full mt-24 rounded-lg shadow-md bg-white p-5">
        <div className="my-3 bg-gradient-to-br from-purple-500 via-sky-light to-sky-dark rounded-md shadow-md w-min text-white p-3">
          <QuestionMarkCircleIcon className="w-8" />
        </div>
        <span className="flex-col space-y-2">
          <label>{t('courses.open_question_textfield_label')}</label>
          <TextEditor
            onChange={setQuestionEditorState}
            editorState={questionEditorState}
          />
        </span>
        <span className="flex-col space-y-2">
          <label>{t('courses.open_question_textfield_explanation')}</label>
          <TextEditor
            onChange={setExplanationEditorState}
            editorState={explanationEditorState}
          />
        </span>
        <span className="flex-col space-y-2">
          <label>{t('courses.open_question_textfield_answer')}</label>
          <TextEditor
            onChange={setAnswerEditorState}
            editorState={answerEditorState}
          />
        </span>
      </div>
    </div>
  );
}

type PreviewProps = {
  selected: boolean;
};

export function Preview(props: PreviewProps) {
  const { selected } = props;
  const { t } = useTranslation();
  return (
    <div
      className={`h-full w-full text-sky mx-auto bg-grey-super_light rounded-md border-sky  ${
        selected ? 'opacity-100 border-3' : 'opacity-50 border-2'
      }`}
    >
      <QuestionMarkCircleIcon className="h-10 w-10 text-sky mx-auto mt-8" />
      <p>{t('courses.open_question')}</p>
    </div>
  );
}

Preview.propTypes = {
  selected: PropTypes.bool.isRequired,
};
